/**
 * Helper functions for performance logging
 */

import { RESOURCE_MATCHERS } from './constants';
import { PerfNode, ResourceTiming } from './types';

/**
 * Checks if a URL matches a pattern (string or regex)
 */
export const matchesPattern = (url: string, pattern: string | RegExp): boolean => {
    return typeof pattern === 'string' ? url.includes(pattern) : pattern.test(url);
};

/**
 * Finds or creates a node along the given path in the tree
 * Creates intermediate nodes if they don't exist
 */
export const findOrCreateNode = (nodes: PerfNode[], path: string[]): PerfNode => {
    const [head, ...rest] = path;
    let node = nodes.find(n => n.key === head);
    if (!node) {
        node = { key: head, steps: [] };
        nodes.push(node);
    }
    if (rest.length === 0) return node;
    if (!node.steps) node.steps = [];
    return findOrCreateNode(node.steps, rest);
};

/**
 * Captures resource performance timings from the Performance API
 * Only captures the first occurrence of each matched resource
 */
export const captureResourceTimings = (): Record<string, ResourceTiming> => {
    const timings: Record<string, ResourceTiming> = {};
    
    try {
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        resources.forEach((resource) => {
            // Try each matcher until one succeeds
            RESOURCE_MATCHERS.some((matcher) => {
                // Skip if already captured (first occurrence only)
                if (timings[matcher.key]) return false;
                
                if (matchesPattern(resource.name, matcher.pattern)) {
                    
                    timings[matcher.key] = {
                        name: resource.name,
                        start: parseFloat((resource.startTime / 1000).toFixed(3)),
                        complete: parseFloat((resource.responseEnd / 1000).toFixed(3)),
                        duration: parseFloat((resource.duration / 1000).toFixed(3))
                    };
                    return true; // Break out of some() - move to next resource
                }
                return false;
            });
        });
    } catch (e) {
        // Silently fail if performance API is not available
    }
    
    return timings;
};

/**
 * Sorts performance nodes by time (START time, or COMPLETE time if START is not present)
 * Recursively sorts nested steps as well
 */
export const sortPerfNodes = (nodes: PerfNode[]): PerfNode[] => {
    // Create a copy to avoid mutating the original
    const sortedNodes = [...nodes].sort((a, b) => {
        const timeA = a.START ?? a.COMPLETE ?? Infinity;
        const timeB = b.START ?? b.COMPLETE ?? Infinity;
        return timeA - timeB;
    });
    
    // Recursively sort nested steps
    return sortedNodes.map(node => ({
        ...node,
        steps: node.steps ? sortPerfNodes(node.steps) : node.steps,
    }));
};

